import { useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import { Card } from "@/components/ui/Card";
import { SettingsSaveRow } from "@/pages/stammdaten/settings/SettingsSaveRow";
import {
  errorMessage,
  type DepositMode,
  type DepositSettlementRule,
  type DepositSettlementTreatment,
  type DepositTaxableBasis,
  type SettingsSectionProps,
  type TenantSettings,
} from "@/pages/stammdaten/settings/tenantSettingsTypes";

const DEFAULT_EARLY: DepositSettlementRule = {
  treatment: "refund",
  retain_percent: "0",
  taxable_basis: "retained",
  stay_percent: "100",
};
const DEFAULT_LATE: DepositSettlementRule = {
  treatment: "damages",
  retain_percent: "100",
  taxable_basis: "retained",
  stay_percent: "100",
};
const DEFAULT_NO_SHOW: DepositSettlementRule = {
  treatment: "taxable",
  retain_percent: "100",
  taxable_basis: "retained",
  stay_percent: "100",
};

function ruleFrom(value: DepositSettlementRule | undefined, fallback: DepositSettlementRule): DepositSettlementRule {
  return {
    treatment: value?.treatment ?? fallback.treatment,
    retain_percent: value?.retain_percent ?? fallback.retain_percent,
    taxable_basis: value?.taxable_basis ?? fallback.taxable_basis,
    stay_percent: value?.stay_percent ?? fallback.stay_percent,
  };
}

export function DepositSection({ settings, canWrite, onSaved }: SettingsSectionProps) {
  const [mode, setMode] = useState<DepositMode>(settings.deposit.mode);
  const [percent, setPercent] = useState(settings.deposit.percent);
  const [fixed, setFixed] = useState(settings.deposit.fixed_amount ?? "");
  const [deadlineDays, setDeadlineDays] = useState(
    String(settings.deposit.cancellation_deadline_days ?? 14),
  );
  const [deadlineTime, setDeadlineTime] = useState(
    settings.deposit.cancellation_deadline_time || "12:00",
  );
  const [early, setEarly] = useState(ruleFrom(settings.deposit.early_cancellation, DEFAULT_EARLY));
  const [late, setLate] = useState(ruleFrom(settings.deposit.late_cancellation, DEFAULT_LATE));
  const [noShow, setNoShow] = useState(ruleFrom(settings.deposit.no_show, DEFAULT_NO_SHOW));
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setMode(settings.deposit.mode);
    setPercent(settings.deposit.percent);
    setFixed(settings.deposit.fixed_amount ?? "");
    setDeadlineDays(String(settings.deposit.cancellation_deadline_days ?? 14));
    setDeadlineTime(settings.deposit.cancellation_deadline_time || "12:00");
    setEarly(ruleFrom(settings.deposit.early_cancellation, DEFAULT_EARLY));
    setLate(ruleFrom(settings.deposit.late_cancellation, DEFAULT_LATE));
    setNoShow(ruleFrom(settings.deposit.no_show, DEFAULT_NO_SHOW));
  }, [settings]);

  async function onSave(e: React.FormEvent) {
    e.preventDefault();
    if (!canWrite) return;
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      const body: {
        mode: DepositMode;
        percent?: string;
        fixed_amount?: string;
        cancellation_deadline_days: number;
        cancellation_deadline_time: string;
        early_cancellation: DepositSettlementRule;
        late_cancellation: DepositSettlementRule;
        no_show: DepositSettlementRule;
      } = {
        mode,
        cancellation_deadline_days: Number.parseInt(deadlineDays.trim() || "14", 10),
        cancellation_deadline_time: deadlineTime || "12:00",
        early_cancellation: early,
        late_cancellation: late,
        no_show: noShow,
      };
      if (mode === "percent") {
        body.percent = percent.trim() || "30";
      } else if (mode === "fixed") {
        body.fixed_amount = fixed.trim() || "0";
      }
      await api.patch<TenantSettings>("/api/v1/settings/deposit", body);
      setSuccess(true);
      onSaved();
    } catch (err) {
      setError(errorMessage(err, "Anzahlungseinstellungen konnten nicht gespeichert werden"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card
      title="Anzahlung"
      description="Die Anzahlung wird mit einer Zahlungsaufforderung eingesammelt und bei der Anreise auf der Rechnung verrechnet. Sagt der Gast ab oder erscheint nicht, schließt eine der Regeln unten die Anzahlung ab."
      bodyClassName="space-y-4"
    >
      <form onSubmit={(e) => void onSave(e)} className="space-y-6">
        <fieldset className="space-y-3" disabled={!canWrite}>
          <legend className="text-sm font-medium text-label">Anzahlungssystem</legend>
          <label className="flex cursor-pointer gap-3 rounded-lg border border-separator p-3 has-[:checked]:border-accent has-[:checked]:bg-accent/5">
            <input
              type="radio"
              name="deposit_mode"
              value="percent"
              checked={mode === "percent"}
              onChange={() => setMode("percent")}
              className="mt-1"
            />
            <span className="space-y-1">
              <span className="block text-sm font-medium text-label">Prozentualer Anzahlungsbetrag</span>
              <span className="block text-sm text-label-secondary">
                z. B. 30 % bei einer Buchung über 200 € → 60 € Anzahlung
              </span>
            </span>
          </label>
          <label className="flex cursor-pointer gap-3 rounded-lg border border-separator p-3 has-[:checked]:border-accent has-[:checked]:bg-accent/5">
            <input
              type="radio"
              name="deposit_mode"
              value="fixed"
              checked={mode === "fixed"}
              onChange={() => setMode("fixed")}
              className="mt-1"
            />
            <span className="space-y-1">
              <span className="block text-sm font-medium text-label">Fester Anzahlungsbetrag</span>
              <span className="block text-sm text-label-secondary">
                Gleicher Reservierungsbetrag unabhängig von der Buchungssumme (höchstens die Buchungssumme)
              </span>
            </span>
          </label>
          <label className="flex cursor-pointer gap-3 rounded-lg border border-separator p-3 has-[:checked]:border-accent has-[:checked]:bg-accent/5">
            <input
              type="radio"
              name="deposit_mode"
              value="first_night"
              checked={mode === "first_night"}
              onChange={() => setMode("first_night")}
              className="mt-1"
            />
            <span className="space-y-1">
              <span className="block text-sm font-medium text-label">Erste Nacht</span>
              <span className="block text-sm text-label-secondary">
                Die Anzahlung ist der Preis der Anreisenacht.
              </span>
            </span>
          </label>
        </fieldset>
        {mode === "percent" ? (
          <label className="flex max-w-xs flex-col gap-1 text-sm">
            Prozentsatz
            <input
              type="number"
              min="0"
              max="100"
              step="0.01"
              required
              className="eh-input"
              value={percent}
              onChange={(e) => setPercent(e.target.value)}
              disabled={!canWrite}
            />
          </label>
        ) : null}
        {mode === "fixed" ? (
          <label className="flex max-w-xs flex-col gap-1 text-sm">
            Fester Betrag (EUR)
            <input
              type="number"
              min="0"
              step="0.01"
              required
              className="eh-input"
              value={fixed}
              onChange={(e) => setFixed(e.target.value)}
              disabled={!canWrite}
            />
          </label>
        ) : null}
        <div className="space-y-3">
          <h3 className="text-sm font-medium text-label">Frist für die frühe Absage</h3>
          <div className="grid max-w-lg gap-4 sm:grid-cols-2">
            <label className="flex flex-col gap-1 text-sm">
              Tage vor der Anreise
              <input
                type="number"
                min="0"
                max="365"
                step="1"
                required
                className="eh-input"
                value={deadlineDays}
                onChange={(e) => setDeadlineDays(e.target.value)}
                disabled={!canWrite}
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              Uhrzeit
              <input
                type="time"
                required
                className="eh-input"
                value={deadlineTime}
                onChange={(e) => setDeadlineTime(e.target.value)}
                disabled={!canWrite}
              />
            </label>
          </div>
          <p className="text-xs leading-relaxed text-label-tertiary">
            Bis zu diesem Zeitpunkt (Europe/Berlin) gilt die Regel für die frühe Absage, danach die
            Regel für die späte Absage. No-Show hat eine eigene Regel.
          </p>
        </div>
        <SettlementRuleFields
          title="Frühe Absage"
          description="Die Buchung wird bis zur Frist storniert."
          rule={early}
          onChange={setEarly}
          disabled={!canWrite}
        />
        <SettlementRuleFields
          title="Späte Absage"
          description="Die Buchung wird nach der Frist storniert und der Platz wieder freigegeben."
          rule={late}
          onChange={setLate}
          disabled={!canWrite}
        />
        <SettlementRuleFields
          title="No-Show"
          description="Der Gast erscheint nicht und der Platz blieb reserviert."
          rule={noShow}
          onChange={setNoShow}
          disabled={!canWrite}
        />
        <SettingsSaveRow
          canWrite={canWrite}
          saving={saving}
          success={success}
          successText="Anzahlungseinstellungen gespeichert."
          error={error}
          label="Anzahlung speichern"
        />
      </form>
    </Card>
  );
}

function SettlementRuleFields({
  title,
  description,
  rule,
  onChange,
  disabled,
}: {
  title: string;
  description: string;
  rule: DepositSettlementRule;
  onChange: (rule: DepositSettlementRule) => void;
  disabled: boolean;
}) {
  return (
    <fieldset className="space-y-3 rounded-lg border border-separator p-3" disabled={disabled}>
      <legend className="px-1 text-sm font-medium text-label">{title}</legend>
      <p className="text-sm text-label-secondary">{description}</p>
      <label className="flex max-w-md flex-col gap-1 text-sm">
        Abschluss der Anzahlung
        <select
          className="eh-select-input"
          value={rule.treatment}
          onChange={(e) =>
            onChange({ ...rule, treatment: e.target.value as DepositSettlementTreatment })
          }
        >
          <option value="refund">Vollständig erstatten, Steuer korrigieren, keine Rechnung</option>
          <option value="damages">Einbehalt als Schadensersatz, ohne Umsatzsteuer</option>
          <option value="taxable">Steuerpflichtige Rechnung, Anzahlung wird verrechnet</option>
        </select>
      </label>
      {rule.treatment !== "refund" ? (
        <label className="flex max-w-xs flex-col gap-1 text-sm">
          Davon einbehalten (%)
          <input
            type="number"
            min="0"
            max="100"
            step="0.01"
            required
            className="eh-input"
            value={rule.retain_percent}
            onChange={(e) => onChange({ ...rule, retain_percent: e.target.value })}
          />
          <span className="text-xs text-label-tertiary">
            Der Rest der eingegangenen Anzahlung wird erstattet. 100 behält den ganzen Betrag.
          </span>
        </label>
      ) : null}
      {rule.treatment === "taxable" ? (
        <div className="grid max-w-lg gap-3 sm:grid-cols-2">
          <label className="flex flex-col gap-1 text-sm">
            Rechnungsbetrag
            <select
              className="eh-select-input"
              value={rule.taxable_basis}
              onChange={(e) =>
                onChange({ ...rule, taxable_basis: e.target.value as DepositTaxableBasis })
              }
            >
              <option value="retained">Nur der einbehaltene Betrag</option>
              <option value="stay_percent">Prozent des Aufenthalts</option>
              <option value="full_stay">Ganzer Aufenthalt</option>
            </select>
          </label>
          {rule.taxable_basis === "stay_percent" ? (
            <label className="flex flex-col gap-1 text-sm">
              Prozent des Aufenthalts
              <input
                type="number"
                min="0"
                max="100"
                step="0.01"
                required
                className="eh-input"
                value={rule.stay_percent}
                onChange={(e) => onChange({ ...rule, stay_percent: e.target.value })}
              />
            </label>
          ) : null}
          <p className="text-xs leading-relaxed text-label-tertiary sm:col-span-2">
            Liegt der Rechnungsbetrag über der einbehaltenen Anzahlung, bleibt die Differenz offen.
            Die schon versteuerte Anzahlung wird auf der Rechnung nur verrechnet.
          </p>
        </div>
      ) : null}
    </fieldset>
  );
}
